import { useState, useEffect, useRef } from 'react';
import { settingsService } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Volume2,
  VolumeX,
  Upload,
  Play,
  Loader2,
  CheckCircle2,
  Music,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface VoiceSettings {
  enabled: boolean;
  successSound: string;
  failSound: string;
  clickSound: string;
  volume: number;
}

const soundLabels: Record<string, { label: string; description: string }> = {
  successSound: { label: 'Success Sound', description: 'Plays when face scan succeeds' },
  failSound: { label: 'Fail Sound', description: 'Plays when face scan fails' },
  clickSound: { label: 'Click Sound', description: 'Plays on button clicks in staff portal' },
};

export default function AdminVoicePage() {
  const [settings, setSettings] = useState<VoiceSettings>({
    enabled: true,
    successSound: 'ale-le-le.mp3',
    failSound: 'sabun.mp3',
    clickSound: 'anime-ahh.mp3',
    volume: 0.6,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [availableFiles, setAvailableFiles] = useState<string[]>([]);
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [voiceSettings, files] = await Promise.all([
        settingsService.getVoiceSettings(),
        settingsService.getVoiceFiles(),
      ]);
      setSettings(voiceSettings);
      setAvailableFiles(files);
    } catch (error) {
      console.error('Error loading voice settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsService.updateVoiceSettings(settings);
      toast({ title: 'Voice settings saved' });
    } catch (error) {
      toast({ title: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (key: string, file: File) => {
    setUploading(key);
    try {
      const result = await settingsService.uploadVoiceFile(key, file);
      setSettings({ ...settings, [key]: result.filename });
      const files = await settingsService.getVoiceFiles();
      setAvailableFiles(files);
      toast({ title: `${soundLabels[key].label} updated` });
    } catch (error) {
      toast({ title: 'Upload failed', variant: 'destructive' });
    } finally {
      setUploading(null);
    }
  };

  const playPreview = (filename: string) => {
    const audio = new Audio(`/voices/${filename}`);
    audio.volume = settings.volume;
    audio.play().catch(() => {});
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Voice Settings</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Manage voice effects and upload custom audio files</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-slate-900 dark:bg-white text-white dark:text-slate-900">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
          Save Settings
        </Button>
      </div>

      {/* Master Toggle */}
      <Card className="bg-white/50 dark:bg-slate-900/40">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.enabled ? (
                <Volume2 className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <VolumeX className="w-6 h-6 text-slate-400" />
              )}
              <div>
                <p className="font-bold text-slate-900 dark:text-white">Voice Effects</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {settings.enabled ? 'All voice effects are enabled' : 'All voice effects are disabled'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
              className={cn(
                'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
                settings.enabled ? 'bg-emerald-600' : 'bg-slate-300 dark:bg-slate-700'
              )}
            >
              <span
                className={cn(
                  'inline-block h-5 w-5 transform rounded-full bg-white transition-transform',
                  settings.enabled ? 'translate-x-6' : 'translate-x-1'
                )}
              />
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Volume Control */}
      <Card className="bg-white/50 dark:bg-slate-900/40">
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <Volume2 className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            <p className="font-bold text-slate-900 dark:text-white">Volume</p>
            <span className="text-sm text-slate-500 dark:text-slate-400 ml-auto">{Math.round(settings.volume * 100)}%</span>
          </div>
          <Slider
            value={[settings.volume * 100]}
            onValueChange={(val) => setSettings({ ...settings, volume: val[0] / 100 })}
            max={100}
            step={5}
            className="w-full"
          />
        </CardContent>
      </Card>

      {/* Individual Sound Settings */}
      {(Object.keys(soundLabels) as Array<keyof typeof soundLabels>).map((key) => (
        <Card key={key} className="bg-white/50 dark:bg-slate-900/40">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Music className="w-4 h-4 text-indigo-500" />
                  <p className="font-bold text-slate-900 dark:text-white">{soundLabels[key].label}</p>
                </div>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">{soundLabels[key].description}</p>

                <div className="flex items-center gap-2">
                  <select
                    value={settings[key]}
                    onChange={(e) => setSettings({ ...settings, [key]: e.target.value })}
                    className="flex-1 h-9 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-3 text-sm"
                  >
                    {availableFiles.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => playPreview(settings[key])}
                    className="h-9 px-3"
                  >
                    <Play className="w-3 h-3" />
                  </Button>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1">
                <input
                  ref={(el) => { fileRefs.current[key] = el; }}
                  type="file"
                  accept=".mp3,.wav,.ogg,.m4a,.aac,.webm"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleUpload(key, file);
                  }}
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => fileRefs.current[key]?.click()}
                  disabled={uploading === key}
                  className="h-9 px-3"
                >
                  {uploading === key ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Upload className="w-3 h-3" />
                  )}
                </Button>
                <span className="text-[9px] text-slate-400">Upload</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
